import { Alert } from '@mui/material';
import { useGet } from 'hooks/use-get';
import React, { useMemo } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { postRegistrationExists } from 'views/production-systems/api/productionSystems';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { Pond } from 'views/production-systems/types/pond.type';
import { RegistrationActionType } from '../types/registration-action-type.enum';

export const REGISTRATION_EXISTS_QUERY_KEY = 'postRegistrationExists';

type Props = {
  actionTypeId: RegistrationActionType;
  ponds: Pond[];
};

const DuplicateRegistrationWarning: React.FC<Props> = ({ actionTypeId, ponds }) => {
  const { globalSiteId, registrationDate } = usePondsState();
  const date = registrationDate.format('YYYY-MM-DD');
  const cycleIds = ponds.map((p) => p.cycleId).filter((id) => id > 0);

  const { data } = useGet(
    [REGISTRATION_EXISTS_QUERY_KEY, globalSiteId, actionTypeId, date, cycleIds],
    () => postRegistrationExists(globalSiteId, actionTypeId, date, cycleIds),
    { enabled: cycleIds.length > 0 }
  );

  const registeredPonds = useMemo(
    () => ponds.filter((p) => data?.some((r) => r.alreadyRegistered && r.cycleId === p.cycleId)),
    [ponds, data]
  );

  if (!registeredPonds.length) return null;

  return (
    <Alert variant="filled" severity="warning">
      <FormattedMessage
        id="productionSystems.farmOverview.duplicateRegistration"
        values={{
          date: <FormattedDate value={registrationDate.toDate()} day="2-digit" month="short" year="numeric" />,
          ponds: registeredPonds.map((p) => p.pondName).join(', '),
        }}
      />
    </Alert>
  );
};

export default DuplicateRegistrationWarning;
